import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client";
import { PaginationState } from "@tanstack/react-table";
import { toast } from "react-toastify";

// Components
import InvoiceFilterPanel from "components/modules/e-commerce/invoices/InvoiceFilterPanel";
import InvoiceSummaryCard from "components/modules/e-commerce/invoices/InvoiceSummaryCard";
import InvoicesTable from "components/tables/InvoicesTable";

// GraphQL queries
import { GET_INVOICES_BY_SELLER } from "api/graphql/queries/invoice/getInvoicesBySeller";
import { GET_INVOICE_KPIS } from "api/graphql/queries/invoice/getInvoiceKPIs";

// Types
export enum InvoiceType {
  Logistic = "LOGISTIC",
  Penalty = "PENALTY",
  All = ""
}

const Invoices: React.FC = () => {
  const { t } = useTranslation();
  
  // State for invoices
  const [invoices, setInvoices] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [sellerId, setSellerId] = useState<string>("");
  
  // State for KPI data
  const [kpiData, setKpiData] = useState<{
    logisticTotal: number;
    logisticCount: number;
    penaltyTotal: number;
    penaltyCount: number;
  }>({
    logisticTotal: 0,
    logisticCount: 0,
    penaltyTotal: 0,
    penaltyCount: 0
  });
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  
  // Filter states
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<InvoiceType | string>(InvoiceType.All);

  // GraphQL query for invoices
  const [getInvoices, { loading }] = useLazyQuery(GET_INVOICES_BY_SELLER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("GraphQL invoices response:", data);
      
      if (data?.getInvoicesBySeller) {
        // Process invoices to ensure valid amount values and correct types
        const processedInvoices = (data.getInvoicesBySeller.invoices || []).map((invoice: any) => {
          // Ensure type is set correctly based on journal_id if not already set
          let type = invoice.type;
          
          // Map journal_id to correct type if needed
          if (invoice.journal_id) {
            if (invoice.journal_id === 76) {
              type = InvoiceType.Logistic;
            } else if (invoice.journal_id === 74) {
              type = InvoiceType.Penalty;
            }
          }
          
          return {
            ...invoice,
            // Set the corrected type
            type: type,
            // Ensure amount is a valid number
            amount: typeof invoice.total === 'number' && !isNaN(invoice.total) ? invoice.total : 0
          };
        });
        
        setInvoices(processedInvoices);
        
        // Set total amount
        const totalAmount = typeof data.getInvoicesBySeller.total_invoices_amount === 'number' && 
          !isNaN(data.getInvoicesBySeller.total_invoices_amount) ? 
          data.getInvoicesBySeller.total_invoices_amount : 0;
          
        setTotalAmount(totalAmount);
        
        // Get total count from pagination
        if (data.getInvoicesBySeller.pagination) {
          setTotalCount(data.getInvoicesBySeller.pagination.total_records);
        } else {
          setTotalCount(processedInvoices.length);
        }
      } else {
        setInvoices([]);
        setTotalCount(0);
        setTotalAmount(0);
      }
    },
    onError: (error) => {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices: " + error.message);
      setInvoices([]);
      setTotalCount(0);
      setTotalAmount(0);
    }
  });
  
  // GraphQL query for KPIs
  const [getKPIs, { loading: loadingKPI }] = useLazyQuery(GET_INVOICE_KPIS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("GraphQL KPIs response:", data);
      
      if (data?.getInvoiceKPIs && data.getInvoiceKPIs.status === "success") {
        setKpiData({
          logisticTotal: data.getInvoiceKPIs.logistic_invoices_amount || 0,
          logisticCount: data.getInvoiceKPIs.logistic_total_records || 0,
          penaltyTotal: data.getInvoiceKPIs.penalty_invoices_amount || 0,
          penaltyCount: data.getInvoiceKPIs.penalty_total_records || 0
        });
      }
    },
    onError: (error) => {
      console.error("Error fetching KPI data:", error);
      toast.error("Failed to fetch invoice statistics: " + error.message);
    }
  });
  
  // Format date for API
  const formatDateForApi = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };
  
  // Fetch invoices based on current filters and pagination
  const fetchInvoices = () => {
    if (!sellerId) return;
    
    const fromDateStr = fromDate ? formatDateForApi(fromDate) : "";
    const toDateStr = toDate ? formatDateForApi(toDate) : "";
    
    // Log the request parameters
    console.log("Fetching invoices with parameters:", { 
      sellerId,
      typeFilter,
      fromDate: fromDateStr, 
      toDate: toDateStr,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize
    });
    
    // Ensure proper type value is sent to API
    // When "ALL" is selected, we should send an empty string
    const apiTypeFilter = typeFilter === InvoiceType.All ? "" : typeFilter;
    
    // Log the adjusted request parameters
    console.log("Adjusted API parameters:", {
      sellerId,
      type: apiTypeFilter,  // This will be "" for All, or "LOGISTIC"/"PENALTY" for others
      fromDate: fromDateStr, 
      toDate: toDateStr
    });
    
    // Call the GraphQL query
    getInvoices({ 
      variables: {
        seller_id: sellerId,
        type: apiTypeFilter,
        from_date: fromDateStr,
        to_date: toDateStr,
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize
      }
    });
  };
  
  // Fetch KPI data
  const fetchKPIData = () => {
    if (!sellerId) return;
    
    const fromDateStr = fromDate ? formatDateForApi(fromDate) : "";
    const toDateStr = toDate ? formatDateForApi(toDate) : "";
    
    console.log("Fetching KPI data with parameters:", { 
      sellerId,
      fromDate: fromDateStr, 
      toDate: toDateStr
    });
    
    // Call the GraphQL query
    getKPIs({ 
      variables: {
        seller_id: sellerId,
        from_date: fromDateStr,
        to_date: toDateStr
      }
    });
  };
  
  // Fetch Seller ID from localStorage
  useEffect(() => {
    const storedSeller = localStorage.getItem("seller");
    if (storedSeller) {
      try {
        const parsedSeller = JSON.parse(storedSeller);
        if (parsedSeller?.seller_id) {
          setSellerId(parsedSeller.seller_id.toString());
        }
      } catch (error) {
        console.error("Error parsing seller data:", error);
        toast.error("Failed to load seller information");
      }
    }
  }, []);
  
  // Initial fetch and refetch on filter/pagination changes
  useEffect(() => {
    if (sellerId) {
      fetchInvoices();
    }
  }, [sellerId, pagination.pageIndex, pagination.pageSize, fromDate, toDate, typeFilter]);
  
  // Fetch KPI data when sellerId or date filters change
  useEffect(() => {
    if (sellerId) {
      fetchKPIData();
    }
  }, [sellerId, fromDate, toDate]);
  
  // Handle page change
  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPageIndex,
    }));
  };
  
  // Format date nicely
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    
    try {
      const date = new Date(dateStr);
      
      // Format date: Mar 27, 2025
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      maximumFractionDigits: 3
    }).format(amount);
  };
  
  // Apply date filter
  const applyDateFilter = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setTypeFilter("");
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  // Custom type filter setter that also resets pagination
  const setTypeFilterAndReset = (type: InvoiceType | string) => {
    console.log("Setting type filter to:", type);
    setTypeFilter(type);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };
  
  return (
    <div className="mb-9">
      {/* Header Section */}
      <div className="mb-4">
        <h2 className="mb-2">{t("invoices.title")}</h2>
        <p className="text-body-tertiary mb-0">{t("invoices.subtitle")}</p>
      </div>
      
      {/* Summary Card */}
      <div className="mb-4">
        <InvoiceSummaryCard 
          kpiData={kpiData}
          loadingKPI={loadingKPI}
          formatCurrency={formatCurrency}
        />
      </div>
      
      {/* Filter Panel */}
      <div className="mb-3">
        <InvoiceFilterPanel 
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilterAndReset}
          applyFilter={applyDateFilter}
          clearAllFilters={clearAllFilters}
        />
      </div>
      
      {/* Invoices Table */}
      <InvoicesTable 
        invoices={invoices}
        loading={loading}
        totalCount={totalCount}
        pagination={pagination}
        handlePageChange={handlePageChange}
        formatDate={formatDate}
        formatCurrency={formatCurrency}
        onTypeFilterChange={setTypeFilterAndReset}
        currentTypeFilter={typeFilter}
      />
    </div>
  );
};

export default Invoices;
